import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import {
    createUser,
    updateUser,
    ROLE_OPTIONS,
} from "../../services/UserAdminService";

const emptyForm = {
    email: "",
    username: "",
    password: "",
    role: 0,
};

/**
 * Modal tạo hoặc sửa user.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Object|null} props.user - nếu có _id → edit mode, null → create mode
 * @param {Function} props.onClose
 * @param {Function} props.onSaved - callback sau khi save thành công
 */
function UserFormModal({ open, user, onClose, onSaved }) {
    const isEdit = !!user?._id;
    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (isEdit) {
            setForm({
                _id: user._id,
                email: user.email || "",
                username: user.username || "",
                password: "",
                role: typeof user.role === "number" ? user.role : 0,
            });
        } else {
            setForm(emptyForm);
        }
        setErrors({});
    }, [open, user, isEdit]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape" && !submitting) onClose?.();
        };
        window.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [open, submitting, onClose]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: name === "role" ? parseInt(value, 10) : value,
        }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    };

    const validate = () => {
        const errs = {};
        const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!form.email?.trim()) errs.email = "Vui lòng nhập email";
        else if (!emailRe.test(form.email.trim())) errs.email = "Email không hợp lệ";

        if (!form.username?.trim()) errs.username = "Vui lòng nhập tên người dùng";

        if (!isEdit && !form.password?.trim()) {
            errs.password = "Vui lòng nhập mật khẩu";
        } else if (form.password && form.password.length < 6) {
            errs.password = "Mật khẩu tối thiểu 6 ký tự";
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate() || submitting) return;

        setSubmitting(true);
        try {
            const payload = {
                email: form.email.trim().toLowerCase(),
                username: form.username.trim(),
                role: form.role,
            };
            if (form.password?.trim()) payload.password = form.password.trim();

            if (isEdit) {
                payload._id = form._id;
                await updateUser(payload);
                toast.success("Cập nhật user thành công");
            } else {
                await createUser(payload);
                toast.success("Tạo user thành công");
            }

            onSaved?.();
            onClose?.();
        } catch (err) {
            const msg =
                err?.response?.data?.message || err.message || "Lưu user thất bại";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4"
            onClick={() => !submitting && onClose?.()}
        >
            <form
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                style={{ animation: "fadeIn 0.15s ease-out" }}
            >
                <div className="px-5 py-4 border-b border-gray-100">
                    <h3 className="text-base font-semibold text-gray-900">
                        {isEdit ? "Chỉnh sửa user" : "Tạo user mới"}
                    </h3>
                </div>

                <div className="p-5 space-y-4">
                    {/* Email */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                            Email *
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            disabled={submitting || isEdit}
                            autoComplete="off"
                            placeholder="user@example.com"
                            className={`w-full px-3 py-2 text-sm border rounded-lg outline-none transition-colors ${errors.email
                                    ? "border-red-500 focus:border-red-500"
                                    : "border-gray-300 focus:border-red-600"
                                } ${isEdit ? "bg-gray-50 cursor-not-allowed" : ""}`}
                        />
                        {errors.email && (
                            <p className="text-xs text-red-600 mt-1">{errors.email}</p>
                        )}
                        {isEdit && (
                            <p className="text-[11px] text-gray-400 mt-1">
                                Email không thể thay đổi sau khi tạo.
                            </p>
                        )}
                    </div>

                    {/* Username */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                            Tên người dùng *
                        </label>
                        <input
                            type="text"
                            name="username"
                            value={form.username}
                            onChange={handleChange}
                            disabled={submitting}
                            placeholder="Nguyễn Văn A"
                            className={`w-full px-3 py-2 text-sm border rounded-lg outline-none transition-colors ${errors.username
                                    ? "border-red-500 focus:border-red-500"
                                    : "border-gray-300 focus:border-red-600"
                                }`}
                        />
                        {errors.username && (
                            <p className="text-xs text-red-600 mt-1">{errors.username}</p>
                        )}
                    </div>

                    {/* Password */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                            Mật khẩu {isEdit ? "(để trống nếu không đổi)" : "*"}
                        </label>
                        <input
                            type="password"
                            name="password"
                            value={form.password}
                            onChange={handleChange}
                            disabled={submitting}
                            autoComplete="new-password"
                            placeholder={isEdit ? "••••••••" : "Ít nhất 6 ký tự"}
                            className={`w-full px-3 py-2 text-sm border rounded-lg outline-none transition-colors ${errors.password
                                    ? "border-red-500 focus:border-red-500"
                                    : "border-gray-300 focus:border-red-600"
                                }`}
                        />
                        {errors.password && (
                            <p className="text-xs text-red-600 mt-1">{errors.password}</p>
                        )}
                    </div>

                    {/* Role */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                            Vai trò
                        </label>
                        <select
                            name="role"
                            value={form.role}
                            onChange={handleChange}
                            disabled={submitting}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-red-600 bg-white"
                        >
                            {ROLE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50">
                    <button
                        type="button"
                        disabled={submitting}
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                    >
                        Hủy
                    </button>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
                    >
                        {submitting
                            ? "Đang lưu…"
                            : isEdit
                                ? "Lưu thay đổi"
                                : "Tạo user"}
                    </button>
                </div>
            </form>
        </div>,
        document.body
    );
}

export default UserFormModal;
